import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { processQuizAnswers } from '../data/scoring';
import { formatProfile } from '../utils/quizLogic';
import { generateTarotCardGemini } from '../services/geminiImageGeneration';
import { getPersonalityTypeNumber, toLatinNumeral } from '../utils/personalityUtils';
import { questions } from '../data/questions';
import html2canvas from 'html2canvas';
import '../App.css';

function ResultsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Helper function to get storage key for this profile
  const getStorageKey = (profileCode) => {
    return `tarot_card_image_${profileCode}`;
  };
  
  // Helper function to get selfie signature storage key
  const getSelfieSigKey = (profileCode) => {
    return `tarot_card_selfie_sig_${profileCode}`;
  };
  
  // Helper function to get selfie signature (first 100 chars of base64)
  const getSelfieSig = (selfieBase64) => {
    if (!selfieBase64) return 'no_selfie';
    return selfieBase64.substring(0, 100);
  };
  
  // Helper function to load saved image from sessionStorage
  const loadSavedImage = (profileCode) => {
    if (!profileCode) return null;
    try {
      const saved = sessionStorage.getItem(getStorageKey(profileCode));
      return saved || null;
    } catch (error) {
      console.error('Error loading saved image from sessionStorage:', error);
      return null;
    }
  };
  
  // Helper function to save image to sessionStorage
  const saveImage = (profileCode, imageUrl, selfieBase64) => {
    if (!profileCode || !imageUrl) return;
    try {
      sessionStorage.setItem(getStorageKey(profileCode), imageUrl);
      // Store selfie signature to detect changes
      sessionStorage.setItem(getSelfieSigKey(profileCode), getSelfieSig(selfieBase64));
    } catch (error) {
      console.error('Error saving image to sessionStorage:', error);
    }
  };
  
  // Helper function to upload image to imgbb for email PDF
  const uploadToImgbb = async (imageUrl) => {
    try {
      console.log('📤 Uploading image to imgbb for email PDF...');
      const apiUrl = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:3001/api/upload-image'
        : '/api/upload-image';
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageBase64: imageUrl })
      });
      
      if (!response.ok) {
        const error = await response.json();
        console.warn('⚠️ Failed to upload to imgbb:', error);
        return null;
      }
      
      const data = await response.json();
      console.log('✅ Image uploaded to imgbb:', data.url);
      
      // Store imgbb URL in sessionStorage for PaymentPage
      sessionStorage.setItem('money_mirror_imgbb_url', data.url);
      return data.url;
    } catch (error) {
      console.warn('⚠️ Error uploading to imgbb:', error);
      return null;
    }
  };
  
  // Initialize image URL from sessionStorage if available
  const initializeImageUrl = (profileCode) => {
    if (!profileCode) return null;
    try {
      const storageKey = `tarot_card_image_${profileCode}`;
      return sessionStorage.getItem(storageKey) || null;
    } catch (error) {
      console.error('Error loading saved image from sessionStorage:', error);
      return null;
    }
  };
  
  const [generatedImageUrl, setGeneratedImageUrl] = useState(() => {
    // Try to get profile from location state to initialize
    const initialResults = location.state?.results;
    const initialProfile = initialResults?.profile;
    return initializeImageUrl(initialProfile) || null;
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState(null);
  const tarotCardRef = useRef(null);
  const generationStartedRef = useRef(false); // Track if generation has been initiated
  
  // Debug: Log state changes
  useEffect(() => {
    console.log('🔄 ResultsPage state:', { 
      generatedImageUrl: generatedImageUrl ? 'SET' : 'NULL', 
      isGenerating, 
      generationError: generationError || 'NONE' 
    });
  }, [generatedImageUrl, isGenerating, generationError]);
  
  // Helper function to save state to sessionStorage
  const saveStateToSessionStorage = (results, answers, selfie) => {
    try {
      if (results) {
        sessionStorage.setItem('money_mirror_results', JSON.stringify(results));
      }
      if (answers && answers.length > 0) {
        sessionStorage.setItem('money_mirror_answers', JSON.stringify(answers));
      }
      if (selfie) {
        sessionStorage.setItem('money_mirror_selfie', selfie);
      }
    } catch (error) {
      console.error('Error saving state to sessionStorage:', error);
    }
  };

  // Helper function to load state from sessionStorage
  const loadStateFromSessionStorage = () => {
    try {
      const savedResults = sessionStorage.getItem('money_mirror_results');
      const savedAnswers = sessionStorage.getItem('money_mirror_answers');
      const savedSelfie = sessionStorage.getItem('money_mirror_selfie');
      
      return {
        results: savedResults ? JSON.parse(savedResults) : null,
        answers: savedAnswers ? JSON.parse(savedAnswers) : null,
        selfie: savedSelfie || null
      };
    } catch (error) {
      console.error('Error loading state from sessionStorage:', error);
      return { results: null, answers: null, selfie: null };
    }
  };

  // Memoize results so it doesn't create a new object reference on every render
  const results = useMemo(() => {
    // First, try location.state (from navigation)
    let computedResults = location.state?.results;
    let computedAnswers = location.state?.answers;
    
    // If no results in location state, try sessionStorage
    if (!computedResults) {
      const savedState = loadStateFromSessionStorage();
      computedResults = savedState.results;
      computedAnswers = computedAnswers || savedState.answers;
    }
    
    // If no results in state, try to recalculate from answers (fallback)
    if (!computedResults && computedAnswers) {
      try {
        computedResults = processQuizAnswers(computedAnswers);
      } catch (error) {
        console.error('Error recalculating results:', error);
      }
    }
    
    return computedResults;
  }, [location.state?.results, location.state?.answers]);

  const { personality, scriptProfile, profile } = results || {};
  const scriptOrder = ['Money Avoidance', 'Money Worship', 'Money Status', 'Money Vigilance'];
  
  // Get selfie and answers from location.state or sessionStorage
  const selfie = useMemo(() => {
    if (location.state?.selfie) {
      return location.state.selfie;
    }
    const savedState = loadStateFromSessionStorage();
    return savedState.selfie || null;
  }, [location.state?.selfie]);
  
  const answers = useMemo(() => {
    if (location.state?.answers && location.state.answers.length > 0) {
      return location.state.answers;
    }
    const savedState = loadStateFromSessionStorage();
    return savedState.answers || [];
  }, [location.state?.answers]);
  
  // Save state to sessionStorage whenever it changes (so it persists across navigation)
  useEffect(() => {
    if (results && (answers.length > 0 || selfie)) {
      saveStateToSessionStorage(results, answers, selfie);
    }
  }, [results, answers, selfie]);

  // Track results page view in PostHog
  useEffect(() => {
    if (window.posthog && profile) {
      window.posthog.capture('results_viewed', {
        personality_type: profile,
        personality_name: personality?.name || ''
      });
    }
  }, [profile, personality]);

  // Safety check for scriptReflections
  const scriptReflections = personality?.scriptReflections || {};

  // Debug: Log when component renders
  useEffect(() => {
    console.log('📄 ResultsPage rendered', { 
      hasResults: !!results, 
      hasAnswers: !!answers,
      navigateType: typeof navigate 
    });
    
    // Attach direct DOM event listener since React onClick isn't working
    const attachButtonHandler = () => {
      const button = document.getElementById('payment-button') || document.querySelector('.primary-button');
      if (button && !button.dataset.handlerAttached) {
        console.log('✅ Attaching direct click handler to button');
        button.dataset.handlerAttached = 'true';
        
        // Remove any existing React handlers and attach our own
        button.onclick = null; // Clear React's handler
        
        // Use onclick property (most reliable)
        button.onclick = function(e) {
          e.preventDefault();
          e.stopPropagation();
          console.log('🎯 Button clicked via direct handler!');
          // Track checkout start in PostHog
          if (window.posthog) {
            window.posthog.capture('checkout_started', {
              personality_type: profile
            });
          }
          navigate('/payment', { state: { results, answers, selfie } });
        };
        
        // Also add addEventListener as backup
        button.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
          console.log('🎯 Button clicked via addEventListener!');
          navigate('/payment', { state: { results, answers, selfie } });
        }, true);
      } else if (button) {
        console.log('⚠️ Handler already attached');
      } else {
        console.log('⚠️ Button not found, retrying...');
        setTimeout(attachButtonHandler, 200);
      }
    };
    
    // Try multiple times to catch button after render
    setTimeout(attachButtonHandler, 100);
    setTimeout(attachButtonHandler, 500);
    setTimeout(attachButtonHandler, 1000);
    setTimeout(attachButtonHandler, 2000);
  }, [results, answers, navigate]);

  // Calculate bar count (1-5) based on overall script score
  // Each script has 3 questions, each answer is 1-5
  // So scores range from 3 (all 1s) to 15 (all 5s)
  // Score 3-5 = 1 bar, 6-8 = 2 bars, 9-11 = 3 bars, 12-14 = 4 bars, 15 = 5 bars
  const getBarCount = (scriptName) => {
    let score = 0;
    let questionCount = 0;
    
    questions.forEach((question, index) => {
      if (question.script === scriptName) {
        questionCount++;
        if (answers[index] !== null && answers[index] !== undefined) {
          score += answers[index];
        }
      }
    });
    
    // Debug logging
    console.log(`Script: ${scriptName}, Score: ${score}, Questions found: ${questionCount}, Answers:`, 
      questions.map((q, i) => q.script === scriptName ? answers[i] : null).filter(a => a !== null)
    );
    
    // Map score to bar count (1-5)
    if (score <= 5) {
      return 1; // Lowest: 1 bar
    } else if (score <= 8) {
      return 2; // Low: 2 bars
    } else if (score <= 11) {
      return 3; // Medium: 3 bars
    } else if (score <= 14) {
      return 4; // High: 4 bars
    } else {
      return 5; // Highest: 5 bars
    }
  };

  // Load saved image from sessionStorage when profile becomes available
  // BUT check if selfie has changed - if so, clear cache and regenerate
  useEffect(() => {
    if (!profile) return;
    
    const savedSelfieSig = sessionStorage.getItem(getSelfieSigKey(profile));
    const currentSelfieSig = getSelfieSig(selfie);
    
    // Check if selfie has changed (regardless of whether we have an image)
    if (savedSelfieSig !== null && savedSelfieSig !== currentSelfieSig) {
      console.log('🔄 Selfie changed! Clearing cached image and regenerating...', {
        savedSig: savedSelfieSig,
        currentSig: currentSelfieSig
      });
      // Clear cached image for this profile
      sessionStorage.removeItem(getStorageKey(profile));
      sessionStorage.removeItem(getSelfieSigKey(profile));
      // Reset state to trigger regeneration
      setGeneratedImageUrl(null);
      generationStartedRef.current = false;
      return; // Exit early - generation useEffect will handle the rest
    }
    
    // Only load saved image if we don't already have one and not generating
    if (!generatedImageUrl && !isGenerating) {
      const savedImageUrl = loadSavedImage(profile);
      if (savedImageUrl) {
        console.log('Loading saved image from sessionStorage for profile:', profile);
        setGeneratedImageUrl(savedImageUrl);
      }
    }
  }, [profile, selfie]); // Depend on both profile AND selfie

  // Generate tarot card image when component mounts (with or without selfie)
  // This hook must be called before any conditional returns
  useEffect(() => {
    // Only generate if we have all required data and haven't already generated
    // Selfie is optional - if not provided, will generate with hooded figure
    // Use ref to prevent double generation from React StrictMode or dependency changes
    if (results && profile && personality?.name && !generatedImageUrl && !generationStartedRef.current) {
      console.log('Starting tarot card generation...', { profile, personality: personality.name, hasSelfie: !!selfie });
      generationStartedRef.current = true; // Mark generation as started
      setIsGenerating(true);
      setGenerationError(null);
      
      generateTarotCardGemini(selfie || null, profile, personality.name, 'fast')
        .then(async (imageUrl) => {
          console.log('Tarot card generated successfully');
          setGeneratedImageUrl(imageUrl);
          // Save to sessionStorage for persistence (with selfie signature)
          saveImage(profile, imageUrl, selfie);
          setIsGenerating(false);
          
          // Upload to imgbb for email PDF (non-blocking)
          uploadToImgbb(imageUrl);
        })
        .catch((error) => {
          console.error('Failed to generate tarot card:', error);
          setGenerationError(error.message || 'Failed to generate tarot card');
          setIsGenerating(false);
          generationStartedRef.current = false; // Allow retry on error
        });
    }
  }, [results, selfie, profile, personality?.name, generatedImageUrl]); // Removed isGenerating from deps

  // If still no results, redirect to quiz (after hooks)
  if (!results) {
    navigate('/quiz');
    return null;
  }

  const handleRetake = () => {
    navigate('/quiz');
  };

  // Helper function to create a clean filename from personality name
  const getFilename = () => {
    if (!personality?.name) {
      return 'tarot-card.png';
    }
    // Remove "The " prefix, convert to lowercase, replace spaces with hyphens, remove special chars
    let filename = personality.name
      .replace(/^The /i, '')
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
    // Ensure .png extension
    if (!filename.endsWith('.png')) {
      filename += '.png';
    }
    return filename;
  };

  // Function to animate card rotation as feedback for download/share actions
  const animateCardFeedback = () => {
    if (!tarotCardRef.current) return;
    
    // Rotate to 0 degrees (straighten)
    tarotCardRef.current.style.transform = 'rotate(0deg)';
    
    // Rotate back to -3 degrees after 300ms
    setTimeout(() => {
      if (tarotCardRef.current) {
        tarotCardRef.current.style.transform = 'rotate(-3deg)';
      }
    }, 300);
  };

  // Function to capture the full tarot card as an image
  // Uses onclone to modify cloned DOM, so live page doesn't change visually
  const captureFullCard = async () => {
    if (!tarotCardRef.current) return null;
    
    try {
      // Get dimensions from the live image for calculations in onclone
      const liveContainer = tarotCardRef.current.querySelector('.results-tarot-container');
      const liveImage = tarotCardRef.current.querySelector('.results-tarot-image');
      
      let containerWidth = 240, containerHeight = 300;
      let naturalWidth = 0, naturalHeight = 0;
      
      if (liveContainer) {
        const rect = liveContainer.getBoundingClientRect();
        containerWidth = rect.width;
        containerHeight = rect.height;
      }
      
      if (liveImage) {
        naturalWidth = liveImage.naturalWidth || liveImage.width;
        naturalHeight = liveImage.naturalHeight || liveImage.height;
      }
      
      // Capture using onclone - all modifications happen on the CLONE, not the live DOM
      const canvas = await html2canvas(tarotCardRef.current, {
        backgroundColor: null,
        scale: 2,
        logging: false,
        useCORS: true,
        allowTaint: true,
        onclone: (clonedDoc, clonedElement) => {
          // Remove rotation on the clone
          clonedElement.style.transform = 'rotate(0deg)';
          
          // Hide personalise button on the clone
          const clonedButton = clonedElement.querySelector('.personalise-card-button');
          if (clonedButton) {
            clonedButton.style.display = 'none';
          }
          
          // Fix image sizing on the clone (html2canvas doesn't handle object-fit well)
          const clonedImage = clonedElement.querySelector('.results-tarot-image');
          const clonedContainer = clonedElement.querySelector('.results-tarot-container');
          
          if (clonedImage && clonedContainer && naturalWidth && naturalHeight) {
            const containerAspect = containerWidth / containerHeight;
            const imageAspect = naturalWidth / naturalHeight;
            
            let renderWidth, renderHeight;
            
            if (imageAspect > containerAspect) {
              renderHeight = containerHeight;
              renderWidth = renderHeight * imageAspect;
            } else {
              renderWidth = containerWidth;
              renderHeight = renderWidth / imageAspect;
            }
            
            // Apply sizing to cloned image only
            clonedImage.style.width = `${renderWidth}px`;
            clonedImage.style.height = `${renderHeight}px`;
            clonedImage.style.minWidth = `${renderWidth}px`;
            clonedImage.style.minHeight = `${renderHeight}px`;
            clonedImage.style.objectFit = 'none';
          }
        }
      });
      
      return canvas.toDataURL('image/png');
    } catch (error) {
      console.error('Error capturing card:', error);
      return null;
    }
  };

  return (
    <div className="app-container">
      {/* Blue gradient overlay */}
      <div className="blue-gradient-overlay"></div>

      <div className="main-content results-content">
        {/* Personality Header - matching landing page structure */}
        <div className="title-section">
          <p className="title-line-1">YOU ARE</p>
          <p className="title-line-2">{personality?.name || 'Unknown'}</p>
        </div>

        {/* Tarot card frame - always visible */}
        <div className="results-tarot-frame" ref={tarotCardRef}>
          {/* Type number at top */}
          {profile && (() => {
            const typeNumber = getPersonalityTypeNumber(profile);
            const latinNumeral = typeNumber ? toLatinNumeral(typeNumber) : '';
            return latinNumeral ? (
              <div className="tarot-frame-number">{latinNumeral}</div>
            ) : null;
          })()}
          
          {/* Image container */}
          <div className="results-tarot-container">
            {isGenerating && (
              <div className="tarot-generating-placeholder shimmer-effect"></div>
            )}
            {generatedImageUrl && !isGenerating && (
              <>
                <img 
                  src={generatedImageUrl} 
                  alt={`${personality?.name} tarot card`}
                  className="results-tarot-image"
                  onError={(e) => {
                    console.error('Error loading generated image:', generatedImageUrl);
                    e.target.style.display = 'none';
                  }}
                  onLoad={() => {
                    console.log('✅ Generated tarot card image loaded successfully');
                  }}
                />
                {/* Personalise button - only show when no selfie was provided */}
                {!selfie && (
                  <button
                    className="personalise-card-button"
                    onClick={() => navigate('/capture', {
                      state: {
                        results,
                        answers
                      }
                    })}
                  >
                    Personalise card
                  </button>
                )}
              </>
            )}
            {generationError && !isGenerating && (
              <>
                <div className="tarot-error-placeholder">
                  <p className="error-text">Unable to generate tarot card</p>
                </div>
                <button
                  className="personalise-card-button"
                  onClick={() => {
                    setGenerationError(null);
                    setGeneratedImageUrl(null);
                    generationStartedRef.current = false; // Reset ref to allow retry
                    // The useEffect will pick this up and retry
                  }}
                >
                  Try again
                </button>
              </>
            )}
          </div>
          
          {/* Personality name box at bottom */}
          {personality?.name && (
            <div className="tarot-frame-name-box">
              <p className="tarot-frame-name">{personality.name}</p>
            </div>
          )}
        </div>

        {/* Download and Share icons - always visible, disabled until image is generated */}
        <div className="tarot-card-icons">
          <div
            className={`icon-wrapper ${!generatedImageUrl || isGenerating ? 'disabled' : ''}`}
            onClick={async () => {
              // Prevent action if image not ready
              if (!generatedImageUrl || isGenerating) {
                return;
              }
              
              // Animate card as feedback
              animateCardFeedback();
              
              // Capture and download the full tarot card
              try {
                const cardImage = await captureFullCard();
                const filename = getFilename();
                console.log('Download filename:', filename);
                console.log('Personality name:', personality?.name);
                
                // Convert data URL to blob for proper download
                const dataURLtoBlob = (dataURL) => {
                  const arr = dataURL.split(',');
                  const mime = arr[0].match(/:(.*?);/)[1];
                  const bstr = atob(arr[1]);
                  let n = bstr.length;
                  const u8arr = new Uint8Array(n);
                  while (n--) {
                    u8arr[n] = bstr.charCodeAt(n);
                  }
                  return new Blob([u8arr], { type: mime });
                };
                
                // Get the image data URL
                const imageDataUrl = cardImage || generatedImageUrl;
                
                // Convert to blob for proper download behavior
                const blob = dataURLtoBlob(imageDataUrl);
                const blobUrl = URL.createObjectURL(blob);
                
                // Create download link
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = filename;
                
                // Trigger download
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                
                // Cleanup blob URL after download starts
                setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
                
                console.log('Download triggered:', filename);
              } catch (error) {
                console.error('Error downloading card:', error);
                alert('Failed to download image. Please try again.');
              }
            }}
            >
              <img 
                src={process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}/data/download-icon.png` : '/data/download-icon.png'}
                alt="Download"
                width="32"
                height="32"
              />
            </div>
            <div
              className={`icon-wrapper ${!generatedImageUrl || isGenerating ? 'disabled' : ''}`}
              onClick={async () => {
                // Prevent action if image not ready
                if (!generatedImageUrl || isGenerating) {
                  return;
                }
                
                // Animate card as feedback
                animateCardFeedback();
                
                // Capture the full tarot card
                const cardImage = await captureFullCard();
                
                if (!cardImage) {
                  // Fallback to just the generated image
                  const response = await fetch(generatedImageUrl);
                  const blob = await response.blob();
                  
                  if (navigator.share && navigator.canShare) {
                    try {
                      const file = new File([blob], getFilename(), { type: 'image/png' });
                      await navigator.share({
                        text: `I'm ${personality?.name || 'a Money Mirror type'}. Find out your subconscious beliefs about money at https://moneymirror.me`,
                        files: [file]
                      });
                    } catch (error) {
                      if (error.name !== 'AbortError') {
                        console.error('Error sharing:', error);
                      }
                    }
                  } else {
                    // Fallback: copy image to clipboard
                    try {
                      await navigator.clipboard.write([
                        new ClipboardItem({ [blob.type]: blob })
                      ]);
                      alert('Image copied to clipboard!');
                    } catch (error) {
                      navigator.clipboard.writeText(window.location.href);
                      alert('Link copied to clipboard!');
                    }
                  }
                  return;
                }
                
                // Try Web Share API with full card image
                if (navigator.share && navigator.canShare) {
                  try {
                    // Convert data URL to blob
                    const response = await fetch(cardImage);
                    const blob = await response.blob();
                    const file = new File([blob], getFilename(), { type: 'image/png' });
                    
                    await navigator.share({
                      text: `I'm ${personality?.name || 'a Money Mirror type'}. Find out your subconscious beliefs about money at https://moneymirror.me`,
                      files: [file]
                    });
                  } catch (error) {
                    if (error.name !== 'AbortError') {
                      console.error('Error sharing:', error);
                      // Fallback: copy image to clipboard
                      try {
                        const response = await fetch(cardImage);
                        const blob = await response.blob();
                        await navigator.clipboard.write([
                          new ClipboardItem({ [blob.type]: blob })
                        ]);
                        alert('Card copied to clipboard!');
                      } catch (clipError) {
                        navigator.clipboard.writeText(window.location.href);
                        alert('Link copied to clipboard!');
                      }
                    }
                  }
                } else {
                  // Fallback: copy image to clipboard
                  try {
                    const response = await fetch(cardImage);
                    const blob = await response.blob();
                    await navigator.clipboard.write([
                      new ClipboardItem({ [blob.type]: blob })
                    ]);
                    alert('Card copied to clipboard!');
                  } catch (error) {
                    // Fallback: copy link
                    navigator.clipboard.writeText(window.location.href);
                    alert('Link copied to clipboard!');
                  }
                }
              }}
            >
              <img 
                src={process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}/data/share-icon.png` : '/data/share-icon.png'}
                alt="Share"
                width="32"
                height="32"
              />
            </div>
          </div>

        {/* Personality Summary */}
        <div className="personality-summary">
          <p className="summary-text">{personality?.summary || ''}</p>
        </div>

        {/* Script Reflections */}
        <div className="script-reflections">
          <h2 className="reflections-title">Your money scripts</h2>
          {scriptOrder.map((script, index) => {
            const scriptLevel = scriptProfile?.[index] === 'H' ? 'High' : 'Low';
            const barCount = getBarCount(script);
            return (
              <div key={script} className="reflection-item">
                <div className="reflection-header">
                  <h3 className="reflection-script">{script}</h3>
                  <div className="script-bars">
                    {[1, 2, 3, 4, 5].map((barNum) => (
                      <div
                        key={barNum}
                        className={`script-bar ${barNum <= barCount ? 'filled' : ''}`}
                      />
                    ))}
                  </div>
                </div>
                <p className="reflection-text">{scriptReflections[script] || 'No reflection available.'}</p>
              </div>
            );
          })}
        </div>

        {/* Next step section */}
        <div className="script-reflections">
          <h2 className="reflections-title">Take the next step</h2>
        </div>

        {/* Action buttons */}
        <div className="results-actions" data-testid="payment-buttons-container">
          <button 
            id="payment-button"
            className="primary-button"
            type="button"
            style={{ zIndex: 10000, position: 'relative', pointerEvents: 'auto' }}
          >
            Get your full report
          </button>
          <button 
            className="secondary-button"
            onClick={() => navigate('/money-scripts')}
          >
            What are money scripts?
          </button>
        </div>

        {/* TEMPORARY: Test PDF Download Button - COMMENTED OUT */}
        {/* <div style={{ marginTop: '40px', textAlign: 'center' }}>
          <button
            className="secondary-button"
            onClick={async () => {
              if (!results || !personality) {
                alert('Results not available. Please complete the quiz first.');
                return;
              }

              try {
                console.log('📄 Generating PDF...');
                console.log('Results data:', { 
                  personality: personality?.name, 
                  profile, 
                  hasImage: !!generatedImageUrl 
                });
                
                // Prepare results data for API
                const resultsData = {
                  personality: {
                    name: personality.name,
                    summary: personality.summary,
                    scriptReflections: personality.scriptReflections
                  },
                  profile: profile,
                  scriptProfile: scriptProfile,
                  scriptScores: results.scriptScores
                };

                // Include tarot card image if available
                const requestBody = {
                  results: resultsData,
                  ...(generatedImageUrl && { tarotCardImageUrl: generatedImageUrl })
                };

                console.log('📤 Sending request to /api/generate-pdf');

                // Call API to generate PDF
                const response = await fetch('/api/generate-pdf', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify(requestBody)
                });

                console.log('📥 Response received:', {
                  status: response.status,
                  statusText: response.statusText,
                  contentType: response.headers.get('content-type'),
                  ok: response.ok
                });

                if (!response.ok) {
                  let errorData;
                  try {
                    errorData = await response.json();
                  } catch (e) {
                    const errorText = await response.text();
                    throw new Error(errorText || `HTTP error! status: ${response.status}`);
                  }
                  throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
                }

                // Get PDF blob and download
                const blob = await response.blob();
                console.log('📦 PDF blob created, size:', blob.size, 'bytes, type:', blob.type);

                if (blob.size === 0) {
                  throw new Error('PDF blob is empty - generation may have failed');
                }

                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${personality.name.toLowerCase().replace(/\s+/g, '-')}-report.pdf`;
                link.style.display = 'none';
                document.body.appendChild(link);
                
                console.log('🖱️ Clicking download link...');
                link.click();
                
                // Clean up after a delay
                setTimeout(() => {
                  document.body.removeChild(link);
                  window.URL.revokeObjectURL(url);
                }, 1000);

                console.log('✅ PDF download triggered successfully');
              } catch (error) {
                console.error('❌ Error generating PDF:', error);
                console.error('Error stack:', error.stack);
                alert(`Failed to generate PDF: ${error.message}\n\nCheck the browser console for details.`);
              }
            }}
            style={{ 
              background: '#ff6b6b', 
              color: 'white', 
              border: 'none',
              fontSize: '14px',
              padding: '12px 24px'
            }}
          >
            [TEST] Download PDF
          </button>
        </div> */}

        {/* Attribution */}
        <div className="description-container">
          <p className="description-text">
            a project by studio strak
          </p>
        </div>
      </div>
    </div>
  );
}

export default ResultsPage;

